import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { UserCircle2, FolderOpen, Bell, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ws, gbp, fmtDate, ROLE_LABELS } from "./api";
import { useWorkspace } from "./WorkspaceContext";
import { PageHeader, SubTabs, Panel, Empty, Loading } from "./ui";
import { useOrgRefs, EmployeeDetails, EmployeeDialog } from "./employeeShared";
import { AgendaList } from "./CalendarPage";

const TABS = [
  { key: "overview", label: "Overview", to: "/crm-app/my-space/overview" },
  { key: "dashboard", label: "Dashboard", to: "/crm-app/my-space/dashboard" },
  { key: "calendar", label: "Calendar", to: "/crm-app/my-space/calendar" },
];

const Overview = () => {
  const { me, isAdmin, refresh } = useWorkspace();
  const refs = useOrgRefs();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [create, setCreate] = useState(false);
  const empId = me?.employee?.employee_id;
  const [first, ...rest] = (me?.name || "").split(" ");
  const initial = useMemo(() => ({ first_name: first, last_name: rest.join(" "), email: me?.email, crm_user_id: me?.crm_user_id }), [me]); // eslint-disable-line
  useEffect(() => { if (empId) ws(`/crm/workspace/employees/${empId}`).then(setData).catch((e) => toast.error(e.message)); }, [empId]);
  useEffect(() => { ws("/crm/workspace/notifications").then((n) => setNotes(n.notifications.slice(0, 8))).catch(() => {}); }, []);
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Panel title="My profile" className="lg:col-span-2" testid="my-profile-panel">
        {empId ? (data ? <EmployeeDetails data={data} refs={refs} /> : <Loading />) : (
          <Empty icon={UserCircle2} title="No employee profile linked to your login yet" testid="my-profile-empty"
            hint={isAdmin ? "Create your employee record to appear in the team directory." : "Ask your Business Owner or Administrator to link your employee record."}
            action={isAdmin && refs.loaded && <Button onClick={() => setCreate(true)} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid="create-my-profile-btn">Create my employee profile</Button>} />
        )}
      </Panel>
      <div className="space-y-4">
        <Panel title="Account" testid="my-account-panel">
          <p className="text-sm font-medium">{me?.name}</p><p className="text-xs text-slate-500">{me?.email}</p>
          <p className="text-xs mt-3"><span className="rounded-full bg-[#0B1530]/[0.06] text-[#0B1530] px-2.5 py-1 font-medium" data-testid="my-role-badge">{ROLE_LABELS[me?.crm_role]}</span></p>
        </Panel>
        <Panel title="Recent activity" testid="my-activity-panel">
          {notes.length === 0 ? <Empty icon={Bell} title="No recent activity" /> : <ul className="space-y-3">{notes.map((n) => <li key={n.notification_id} className="text-sm">{n.title}<p className="text-[11px] text-slate-400">{fmtDate(n.created_at, true)}</p></li>)}</ul>}
        </Panel>
        <Panel title="My documents" testid="my-documents-panel">
          <Empty icon={FolderOpen} title="Coming in Phase 2" hint="Personal documents will appear here." action={<Link to="/crm-app/documents" className="text-xs text-[#0B1530] underline">Learn more</Link>} />
        </Panel>
      </div>
      {isAdmin && <EmployeeDialog open={create} onOpenChange={setCreate} refs={refs} editing={null} prefix="" initial={initial}
        onSaved={() => { refresh(); refs.reload(); }} />}
    </div>
  );
};

const MyDashboard = () => {
  const [r, setR] = useState(null);
  useEffect(() => { ws("/crm/workspace/reports/my").then(setR).catch((e) => toast.error(e.message)); }, []);
  if (!r) return <Loading />;
  const tiles = [["Deals created", r.deals_created], ["Won", `${r.won_count} · ${gbp(r.won_value)}`], ["Open pipeline", gbp(r.open_value)], ["Win rate", `${r.win_rate}%`], ["Open tasks", r.tasks_open], ["Completed tasks", r.tasks_done]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-dashboard">
      {tiles.map(([l, v]) => <div key={l} className="bg-white rounded-xl border border-slate-200/80 p-5"><p className="text-[11px] uppercase tracking-wider text-slate-500">{l}</p><p className="font-heading text-2xl font-semibold text-[#0B1530] mt-2">{v}</p></div>)}
    </div>
  );
};

export default function MySpacePage() {
  const { tab = "overview" } = useParams();
  return (
    <div data-testid="my-space-page">
      <PageHeader title="My Space" subtitle="Your profile, activity and schedule" actions={<Link to="/crm-app/tasks"><Button variant="outline"><ListChecks className="h-4 w-4 mr-1" />My tasks</Button></Link>} />
      <SubTabs tabs={TABS} />
      {tab === "overview" && <Overview />}
      {tab === "dashboard" && <MyDashboard />}
      {tab === "calendar" && <Panel><AgendaList mine /></Panel>}
    </div>
  );
}
